import { isSupportedLocale } from '@/contexts/i18n/utils';

export type EnterHomeRuntimeEnv = 'dev' | 'beta' | 'production';

export const ENTER_HOME_ENV_ORIGINS: Record<EnterHomeRuntimeEnv, string> = {
    dev: 'https://converge-app.dev.knoffice.tech',
    beta: 'https://beta.converge.ai',
    production: 'https://converge.ai',
};

export const ENTER_HOME_PRICING_PATH = '/pricing';

const DEV_HOSTS = new Set(['codia-web.preview.dev.knoffice.tech', 'localhost', 'localhost:3000', '127.0.0.1', '127.0.0.1:3000']);
const BETA_HOSTS = new Set(['beta.enter.converge.ai', 'agent.converge.ai']);
const PRODUCTION_HOSTS = new Set(['enter.converge.ai', 'converge.ai']);

function getCurrentLocationValue(): string | undefined {
    if (typeof window !== 'undefined') {
        return window.location.href || window.location.origin;
    }

    return process.env.NEXT_PUBLIC_AUTH0_REDIRECT_URI;
}

function getHostParts(value?: string | null): { host: string; hostname: string } | null {
    if (!value) return null;

    try {
        const url = value.includes('://') ? new URL(value) : new URL(`https://${value}`);
        return {
            host: url.host,
            hostname: url.hostname,
        };
    } catch {
        return null;
    }
}

export function resolveEnterHomeRuntimeEnv(value: string | null | undefined = getCurrentLocationValue()): EnterHomeRuntimeEnv {
    const hostParts = getHostParts(value);
    if (!hostParts) return 'production';

    const { host, hostname } = hostParts;

    // 根据当前官网运行域名判断目标环境，保证 Header/Footer 的 Pricing 链接不散落硬编码。
    if (DEV_HOSTS.has(host) || DEV_HOSTS.has(hostname)) {
        return 'dev';
    }

    if (BETA_HOSTS.has(hostname)) {
        return 'beta';
    }

    if (PRODUCTION_HOSTS.has(hostname)) {
        return 'production';
    }

    return 'dev';
}

export function getEnterHomeEnvironmentOrigin(value?: string | null): string {
    return ENTER_HOME_ENV_ORIGINS[resolveEnterHomeRuntimeEnv(value)];
}

function normalizePricingPath(pathname: string): string {
    try {
        const url = new URL(pathname);
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return pathname.startsWith('/') ? pathname : `/${pathname}`;
    }
}

export interface BuildEnterHomePricingUrlOptions {
    businessTraceId?: string;
    entry?: string;
    locale?: string | null;
    returnTo?: string | null;
}

export type EnterHomePricingNavigationMode = 'new_window' | 'same_window_fallback';

export type EnterHomePricingNavigationResult =
    | {
          status: 'committed';
          mode: EnterHomePricingNavigationMode;
          popupBlocked: boolean;
      }
    | {
          status: 'failed';
          reason: 'window_unavailable' | 'navigation_exception';
          popupBlocked: boolean;
      };

export interface EnterHomePricingBrowserWindow {
    open(url?: string | URL, target?: string, features?: string): Window | null;
    location: Pick<Location, 'assign'>;
}

export interface EnterHomePricingNavigationTrackingDetails {
    businessTraceId: string;
    entry: string;
    result: EnterHomePricingNavigationResult;
}

export function openEnterHomePricingWindow(
    url: string,
    browserWindow: EnterHomePricingBrowserWindow | null =
        typeof window === 'undefined' ? null : window
): EnterHomePricingNavigationResult {
    if (!browserWindow) {
        return {
            status: 'failed',
            reason: 'window_unavailable',
            popupBlocked: false,
        };
    }

    let popupBlocked = false;
    try {
        const openedWindow = browserWindow.open(url, '_blank');
        if (openedWindow && !openedWindow.closed) {
            try {
                openedWindow.opener = null;
            } catch {
                // The destination is still committed even if opener isolation is browser-managed.
            }
            return {
                status: 'committed',
                mode: 'new_window',
                popupBlocked: false,
            };
        }
        popupBlocked = !openedWindow;
    } catch {
        // A blocked popup may throw instead of returning null in some browsers.
        popupBlocked = true;
    }

    try {
        browserWindow.location.assign(url);
        return {
            status: 'committed',
            mode: 'same_window_fallback',
            popupBlocked,
        };
    } catch {
        return {
            status: 'failed',
            reason: 'navigation_exception',
            popupBlocked,
        };
    }
}

export function buildEnterHomePricingNavigationTrack({
    businessTraceId,
    entry,
    result,
}: EnterHomePricingNavigationTrackingDetails) {
    const commonProps = {
        product_type: 'Enter',
        business_trace_id: businessTraceId,
        entry,
        popup_blocked: result.popupBlocked,
    };

    if (result.status === 'failed') {
        return {
            eventName: 'pricing_navigation_failed' as const,
            props: {
                ...commonProps,
                reason: result.reason,
            },
            options: {},
        };
    }

    return {
        eventName: 'pricing_navigation_started' as const,
        props: {
            ...commonProps,
            navigation_mode: result.mode,
        },
        options:
            result.mode === 'same_window_fallback'
                ? { sendImmediately: true as const }
                : {},
    };
}

export function buildEnterHomePricingUrl(
    pathname = ENTER_HOME_PRICING_PATH,
    currentLocation?: string | null,
    options: BuildEnterHomePricingUrlOptions = {}
): string {
    const url = new URL(normalizePricingPath(pathname), getEnterHomeEnvironmentOrigin(currentLocation));
    const returnTo = options.returnTo;

    url.searchParams.set('product', 'enter');
    if (options.entry) {
        url.searchParams.set('entry', options.entry);
    }
    if (options.businessTraceId) {
        url.searchParams.set('business_trace_id', options.businessTraceId);
    }
    if (options.locale && isSupportedLocale(options.locale)) {
        url.searchParams.set('locale', options.locale);
    }
    if (returnTo) {
        url.searchParams.set('returnTo', returnTo);
    }

    return url.toString();
}
